import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createSlots as _createSlots } from "vue"

const _hoisted_1 = { id: "dialogProgramDetail" }
const _hoisted_2 = { class: "sr-only" }
const _hoisted_3 = {
  key: 0,
  lang: "ja"
}
const _hoisted_4 = { class: "text-no-wrap" }
const _hoisted_5 = {
  key: 0,
  lang: "ja"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "row" }
const _hoisted_8 = { class: "center-vertically" }
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "row justify-between q-mb-xs"
}
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { key: 1 }

import type { PropType } from 'vue';
import { computed, toRefs, ref, onBeforeUnmount, watch } from 'vue';
import { QCarousel } from 'quasar';

// Stores
import { useUiStore } from 'src/stores/ui';
import { useSecurityStore } from '@stores/security';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// PERSONAL COMPONENTS
import QBtnValid from '@components/form/QBtnValid.vue';
import DialogVideoUpload from '@components/dialog/program/DialogVideoUpload.vue';
import DialogVideoRules from '@components/dialog/program/DialogVideoRules.vue';
import DiagGeneric from '@components/dialog/DiagGeneric.vue';
import QVideoCustom from '@components/QVideoCustom.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// PERSONAL LIBS
import { IProgram, IUser, IVideo } from '@shared/interface/model.interface';
import { $t } from 'src/i18n/utils';
import { RoleEnum } from '@shared/enum/role.enum';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { VideoStatusEnum } from '@shared/enum/video-status.enum';
import { displayDialogError, displayNotifSuccess } from '@shared/lib/notify.lib';
import programRepository from '@data/repository/program.repository';
import endpoints from 'src/shared/service/endpoint/endpoints.class';
import {
  
  IDiagGenericResponse,
  IQDialogTemplate,
} from '@shared/interface/qDialogTemplate.interface';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { FormNameEnum } from '@shared/enum/form-name.enum';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';

/**
 * Events
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogProgramDetail',
  props: {
  show: { type: Boolean, required: true, default: false },
  program: { type: Object as PropType<IProgram>, required: true },
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

// VENDOR
const emits = __emit;

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Stores
 */
const uiStore = useUiStore();
const securityStore = useSecurityStore();

/**
 * HTML Elements
 */
const qCarouselRef = ref<QCarousel>();
const qDialogTemplate = ref<IQDialogTemplate>();

/**
 * Properties
 */
const props = __props;
const { program, show } = toRefs(props);

/**
 * Reactive data
 */
const showVideoUploadDialog = ref(false);
const showVideoRulesDialog = ref(false);
const showPrgEditDialog = ref(false);
const loading = ref(false);
const slide = ref();
const videos = ref<IVideo[]>([]);

/**
 * Computed properties
 */
const currentSlide = computed<number>(() =>
  videos.value.findIndex((v) => v.fileName === slide.value),
);
const connectedUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const isOnline = computed(() => sessionManager.isOnline.value === true);
const canAddVideos = computed<boolean>(
  () =>
    connectedUser.value?.roles != null &&
    connectedUser.value.roles.includes(RoleEnum.ROLE_VIDEO_ADD),
);
const canDisplayVideos = computed<boolean>(
  () =>
    connectedUser.value?.roles != null &&
    connectedUser.value.roles.includes(RoleEnum.ROLE_READ),
);
const canEditProgram = computed<boolean>(
  () =>
    connectedUser.value?.roles != null &&
    connectedUser.value.roles.includes(RoleEnum.ROLE_PRG_EDIT),
);
const hasVideosToValidate = computed<boolean>(
  () =>
    (
      program.value?.videos?.filter(
        (v) => v.status === VideoStatusEnum.TO_VALIDATE,
      ) ?? []
    ).length > 0,
);
const hasValidatedVideos = computed<boolean>(
  () =>
    (
      program.value.videos?.filter(
        (v) => v.status === VideoStatusEnum.VALIDATED,
      ) ?? []
    ).length > 0,
);
const nbValidationWaiting = computed<number>(
  () =>
    (
      program.value.videos?.filter(
        (v) => v.status === VideoStatusEnum.TO_VALIDATE,
      ) ?? []
    ).length,
);
const nbValidatedVideo = computed<number>(
  () =>
    (
      program.value.videos?.filter(
        (v) => v.status === VideoStatusEnum.VALIDATED,
      ) ?? []
    ).length,
);

/**
 * Dialog show/hide functions
 */
function onClose() {
  emits('close');
}

function onBtnClose() {
  emits('close');
}

function onShowVideoRulesDialog() {
  showVideoRulesDialog.value = true;
  emits('close');
}

function onShowPrgEditDialog() {
  showPrgEditDialog.value = true;
}

function onHidePrgEditDialog() {
  showPrgEditDialog.value = false;
}

/**
 * Methods
 */
function goPreviousVideo() {
  if (qCarouselRef.value) {
    qCarouselRef.value.previous();
  }
}

function goNextVideo() {
  if (qCarouselRef.value) {
    qCarouselRef.value.next();
  }
}

function showUploader() {
  showVideoRulesDialog.value = false;
  showVideoUploadDialog.value = true;
}

function setVideoUrl(video: IVideo) {
  const baseURL = `${process.env.BACKEND.host ?? ''}${
    process.env.BACKEND.port ? ':' + process.env.BACKEND.port : ''
  }${process.env.BACKEND.context ?? ''}`;

  return (
    baseURL +
    endpoints.video.getStreamedVideo.addParams([video.id ?? 0]).getUrl()
  );
}

function defineBgColor(): string {
  if (program?.value != null) {
    return `belt-${program.value.beltCode}-${program.value.gradeLevel}`;
  } else {
    return 'belt-yellow-1';
  }
}

async function getVideoSource() {
  videos.value = [];

  program.value.videos?.forEach((video) => {
    if (video.status === VideoStatusEnum.VALIDATED) {
      video.windowUrl = setVideoUrl(video);
      videos.value.push(video);
    }
  });

  if ((program.value.videos ?? []).length > 0) {
    const validatedVideos =
      program.value.videos?.filter(
        (v) => v.status === VideoStatusEnum.VALIDATED,
      ) ?? [];
    if (validatedVideos.length > 0) {
      slide.value = validatedVideos[0].fileName;
    }
  }
}

function uploadVideoDone() {
  uiStore.programs?.forEach((prog) => {
    if (prog.id === program.value?.id) {
      prog.videos?.forEach((video) => {
        if (
          program.value?.videos != null &&
          program.value?.videos.find((v) => v.id === video.id) == null
        ) {
          program.value.videos = [...program.value.videos, video];
        }
      });
    }
  });
}

async function doUpdateProgram(dialogReturn?: IDiagGenericResponse ) {
  const newProgram: IProgram = { ...program.value };
  newProgram.comment = dialogReturn?.inputTextarea ?? '';
  newProgram.csrf = (await securityStore.getCsrf(FormNameEnum.PROGRAM_COMMENT))?.token

  await programRepository
    .putProgram(newProgram)
    .then(() => {
      program.value.comment = newProgram.comment
      displayNotifSuccess('Programme mis à jour avec succès')
    })
    .catch((error) =>
      displayDialogError({
        title: 'Erreur',
        message: error.message ?? 'null',
      }),
    )
    .finally(() => (showPrgEditDialog.value = false));
}

watch(
  () => program.value,
  () => getVideoSource(),
  { immediate: true },
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  program.value.videos?.forEach((video) => {
    video.windowUrl = '';
  });
});

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_markup_table = _resolveComponent("q-markup-table")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_carousel_slide = _resolveComponent("q-carousel-slide")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QDialogTemplate, {
      ref_key: "qDialogTemplate",
      ref: qDialogTemplate,
      "show-dialog": _unref(show),
      title: 'Détails du programme',
      maximized: _unref(isMobile),
      persistent: false,
      "dialog-size": _unref(DialogSizeEnum).LARGE,
      onClose: _cache[8] || (_cache[8] = ($event: any) => (onClose()))
    }, _createSlots({
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_q_card, {
            bordered: "",
            class: "q-ma-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_markup_table, {
                square: "",
                dense: "",
                class: "markup-table q-mx-none no-scroll",
                flat: ""
              }, {
                default: _withCtx(() => [
                  _createElementVNode("caption", _hoisted_2, [
                    _createElementVNode("span", null, [
                      _cache[15] || (_cache[15] = _createTextVNode("Tableau de détail du programme ")),
                      (_unref(program)?.label != null)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(program)?.label), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true)
                    ])
                  ]),
                  _createElementVNode("tbody", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", {
                        scope: "row",
                        class: _normalizeClass(['text-left', defineBgColor()])
                      }, " Ceinture : ", 2 /* CLASS */),
                      _createElementVNode("td", null, _toDisplayString(_unref($t)(
                      `generic.beltLabel.${_unref(program)?.beltCode || 'yellow'}`,
                    )?.toUpperCase() || 'Aucune ceinture pour ce programme') + " (" + _toDisplayString(_unref(program)?.gradeOrder) + "e " + _toDisplayString(_unref(program)?.isKyu ? 'Kyu' : 'Dan') + ") ", 1 /* TEXT */)
                    ]),
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", {
                        scope: "row",
                        class: _normalizeClass(['text-left', defineBgColor()])
                      }, " Programme : ", 2 /* CLASS */),
                      _createElementVNode("td", _hoisted_4, [
                        (_unref(program)?.label != null)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(program)?.label), 1 /* TEXT */))
                          : (_openBlock(), _createElementBlock("span", _hoisted_6, "Aucune libellé pour ce programme"))
                      ])
                    ]),
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", {
                        scope: "row",
                        class: _normalizeClass(['text-left', defineBgColor()])
                      }, " Commentaires : ", 2 /* CLASS */),
                      _createElementVNode("td", _hoisted_7, [
                        _createElementVNode("div", _hoisted_8, [
                          _createTextVNode(_toDisplayString(_unref(program)?.comment || 'Aucune commentaire pour ce programme') + " ", 1 /* TEXT */),
                          (canEditProgram.value)
                            ? (_openBlock(), _createBlock(_component_q_btn, {
                                key: 0,
                                size: "12px",
                                flat: "",
                                dense: "",
                                round: "",
                                disable: isOnline.value === false,
                                icon: "edit",
                                class: "set-outline",
                                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (onShowPrgEditDialog()), ["stop"])),
                                title: 
                        isOnline.value
                          ? `Modifier le commentaire de '${_unref(program)?.label}'`
                          : 'Fonctionnalité indisponible hors-ligne'
                      
                              }, null, 8 /* PROPS */, ["disable", "title"]))
                            : _createCommentVNode("v-if", true)
                        ])
                      ])
                    ])
                  ])
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_card, {
            bordered: "",
            class: "q-ma-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, {
                class: _normalizeClass(defineBgColor())
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Vidéos de référence" + _toDisplayString(canDisplayVideos.value ? ` (${nbValidatedVideo.value})` : '') + " ", 1 /* TEXT */),
                  (!canDisplayVideos.value)
                    ? (_openBlock(), _createBlock(_component_q_icon, {
                        key: 0,
                        name: "lock",
                        size: "xs"
                      }))
                    : _createCommentVNode("v-if", true),
                  (canAddVideos.value)
                    ? (_openBlock(), _createBlock(_component_q_btn, {
                        key: 1,
                        id: "programVideoAddButton",
                        disable: isOnline.value === false,
                        class: "q-ml-sm text-white set-outline",
                        rounded: "",
                        color: "primary",
                        icon: "add",
                        size: "sm",
                        label: "Ajouter une vidéo",
                        title: 
                isOnline.value
                  ? `Ajouter une vidéo à '${_unref(program)?.label}'`
                  : 'Fonctionnalité indisponible hors-ligne'
              ,
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (onShowVideoRulesDialog()))
                      }, null, 8 /* PROPS */, ["disable", "title"]))
                    : _createCommentVNode("v-if", true)
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["class"]),
              _createVNode(_component_q_separator, {
                role: "separator",
                "aria-hidden": "true"
              }),
              _createCommentVNode(" Videos "),
              _createVNode(_component_q_card_section, null, {
                default: _withCtx(() => [
                  (hasValidatedVideos.value || hasVideosToValidate.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        (canDisplayVideos.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              (nbValidationWaiting.value > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(nbValidationWaiting.value) + " vidéo" + _toDisplayString(nbValidationWaiting.value > 1 ? 's' : '') + " en attente de validation pour ce programme ", 1 /* TEXT */))
                                : _createCommentVNode("v-if", true),
                              _createCommentVNode(" Carousel controls "),
                              (nbValidatedVideo.value > 1)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                    _createVNode(_component_q_btn, {
                                      size: "sm",
                                      rounded: "",
                                      color: "secondary",
                                      icon: "arrow_back",
                                      class: "set-outline",
                                      onClick: _cache[2] || (_cache[2] = ($event: any) => (goPreviousVideo())),
                                      disable: currentSlide.value == 0,
                                      title: "Aller à la vidéo précédente"
                                    }, null, 8 /* PROPS */, ["disable"]),
                                    _createElementVNode("span", null, "Vidéo : " + _toDisplayString(currentSlide.value + 1) + " / " + _toDisplayString(videos.value.length), 1 /* TEXT */),
                                    _createVNode(_component_q_btn, {
                                      size: "sm",
                                      rounded: "",
                                      color: "secondary",
                                      class: "set-outline",
                                      "icon-right": "arrow_forward",
                                      disable: currentSlide.value == videos.value.length - 1,
                                      onClick: _cache[3] || (_cache[3] = ($event: any) => (goNextVideo())),
                                      title: "Aller à la vidéo suivante"
                                    }, null, 8 /* PROPS */, ["disable"])
                                  ]))
                                : _createCommentVNode("v-if", true),
                              _createCommentVNode(" Carousel "),
                              (hasValidatedVideos.value)
                                ? (_openBlock(), _createBlock(_unref(QCarousel), {
                                    key: 2,
                                    modelValue: slide.value,
                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((slide).value = $event)),
                                    ref_key: "qCarouselRef",
                                    ref: qCarouselRef,
                                    animated: "",
                                    swipeable: "",
                                    "transition-prev": "slide-right",
                                    "transition-next": "slide-left",
                                    "control-color": "primary",
                                    class: "rounded-borders text-center full-height"
                                  }, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(videos.value, (video, index) => {
                                        return (_openBlock(), _createBlock(_component_q_carousel_slide, {
                                          key: index,
                                          name: video.fileName,
                                          class: "text-center"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(QVideoCustom, {
                                              video: video,
                                              "program-label": _unref(program).label ?? '',
                                              onLoaded: _cache[4] || (_cache[4] = ($event: any) => (loading.value = false)),
                                              onLoading: _cache[5] || (_cache[5] = ($event: any) => (loading.value = true))
                                            }, null, 8 /* PROPS */, ["video", "program-label"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["name"]))
                                      }), 128 /* KEYED_FRAGMENT */))
                                    ]),
                                    _: 1 /* STABLE */
                                  }, 8 /* PROPS */, ["modelValue"]))
                                : _createCommentVNode("v-if", true)
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_13, " Les vidéos sont réservées aux adhérents du club (" + _toDisplayString(nbValidatedVideo.value) + " vidéo" + _toDisplayString(nbValidatedVideo.value > 1 ? 's' : '') + " pour ce programme). ", 1 /* TEXT */))
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_14, "Aucune vidéo disponible pour le moment"))
                ]),
                _: 1 /* STABLE */
              })
            ]),
            _: 1 /* STABLE */
          })
        ])
      ]),
      _: 2 /* DYNAMIC */
    }, [
      (!_unref(isMobile))
        ? {
            name: "footer",
            fn: _withCtx(() => [
              _createVNode(_component_q_card, {
                flat: "",
                class: "no-border-radius"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_separator),
                  _createVNode(_component_q_card_actions, { align: "right" }, {
                    default: _withCtx(() => [
                      _createVNode(QBtnValid, {
                        label: "Fermer",
                        onClick: _cache[7] || (_cache[7] = ($event: any) => (onBtnClose()))
                      })
                    ]),
                    _: 1 /* STABLE */
                  })
                ]),
                _: 1 /* STABLE */
              })
            ]),
            key: "0"
          }
        : undefined
    ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["show-dialog", "maximized", "dialog-size"]),
    _createVNode(DialogVideoUpload, {
      show: showVideoUploadDialog.value,
      program: _unref(program),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (showVideoUploadDialog.value = false)),
      onCancel: _cache[10] || (_cache[10] = ($event: any) => (showVideoUploadDialog.value = false)),
      onSuccess: uploadVideoDone
    }, null, 8 /* PROPS */, ["show", "program"]),
    _createVNode(DialogVideoRules, {
      show: showVideoRulesDialog.value,
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showVideoRulesDialog.value = false)),
      onCancel: _cache[12] || (_cache[12] = ($event: any) => (showVideoRulesDialog.value = false)),
      onSuccess: showUploader
    }, null, 8 /* PROPS */, ["show"]),
    _createVNode(DiagGeneric, {
      show: showPrgEditDialog.value,
      title: 'Modifier un commentaire',
      message: `Modifier le commentaire de '${_unref(program)?.label}'.`,
      "manage-loading": true,
      "input-prompt": {
      model: _unref(program)?.comment ?? '',
      type: _unref(InputFiedTypeEnum).TEXTAREA,
      label: 'Saisir le nouveau commentaire',
      required: false,
    },
      onClose: _cache[13] || (_cache[13] = ($event: any) => (onHidePrgEditDialog())),
      onCancel: _cache[14] || (_cache[14] = ($event: any) => (onHidePrgEditDialog())),
      onValid: doUpdateProgram
    }, null, 8 /* PROPS */, ["show", "message", "input-prompt"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})